import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { fileUrl, BACKEND_URL } from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import {
  Loader2, ChevronLeft, MapPin, Mail, Phone, Car, Calendar, FileText, Download, ShieldCheck, ExternalLink, X, Printer
} from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const STATUS_LABEL = { nuova: "Nuova", presa_in_carico: "Presa in carico", in_lavorazione: "In lavorazione", chiusa: "Chiusa" };

export default function PracticeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/practices/${id}`);
      setData(data);
    } catch (err) {
      toast.error("Pratica non trovata");
      navigate("/dashboard");
    }
  };

  useEffect(() => { load(); }, [id]);

  const changeStatus = async (newStatus) => {
    if (newStatus === data.practice.status) return;
    if (newStatus === "chiusa") {
      if (!confirm("Chiudere la pratica? Verrà generato lo ZIP con PDF + foto.")) return;
    }
    setUpdating(true);
    try {
      const res = await api.patch(`/practices/${id}/status`, { status: newStatus });
      setData({ ...data, practice: res.data });
      toast.success("Stato aggiornato");
    } catch (e) {
      toast.error("Errore aggiornamento");
    } finally {
      setUpdating(false);
    }
  };

  const sendOtpRequest = async () => {
    try {
      await api.post(`/practices/${id}/resend-otp`);
      toast.success("OTP inviato al cliente via email");
    } catch {
      toast.error("Errore invio OTP");
    }
  };

  const openPrintablePdf = async () => {
    try {
      const token = localStorage.getItem("gs_token");
      const res = await fetch(`${BACKEND_URL}/api/practices/${id}/printable-pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      // Open in new tab — most browsers will show built-in PDF viewer with print button
      const w = window.open(url, "_blank");
      if (!w) {
        // popup blocked → fallback download
        const a = document.createElement("a");
        a.href = url;
        a.download = `Pratica-${data.practice.code}.pdf`;
        a.click();
      }
      // Revoke after a delay so the new tab has time to load
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      toast.error("Errore generazione PDF");
    }
  };

  const downloadZip = async () => {
    try {
      const token = localStorage.getItem("gs_token");
      const res = await fetch(`${BACKEND_URL}/api/practices/${id}/zip`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${data.practice.code}.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Errore download ZIP");
    }
  };

  if (!data) {
    return (
      <DashboardLayout>
        <div className="p-8 flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-[#2456C6]" />
        </div>
      </DashboardLayout>
    );
  }

  const p = data.practice;
  const photos = data.photos || [];

  return (
    <DashboardLayout role="consultant">
      <div className="p-4 sm:p-8">
        <Link to="/dashboard" data-testid="back-to-list-link"
          className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[#2456C6] mb-4">
          <ChevronLeft className="w-4 h-4" /> Torna all&apos;elenco
        </Link>

        {/* Header */}
        <div className="card-gs mb-5">
          <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-1">Pratica</div>
              <h1 className="text-3xl font-black text-[#1E293B]" data-testid="detail-code">{p.code}</h1>
              <div className="text-sm text-slate-500 mt-1 flex items-center gap-1">
                <Calendar className="w-3 h-3" /> {new Date(p.created_at).toLocaleString("it-IT")}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Select value={p.status} onValueChange={changeStatus} disabled={updating}>
                <SelectTrigger className="w-[200px] h-12 font-bold" data-testid="status-select-trigger">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(STATUS_LABEL).map(([k, v]) => (
                    <SelectItem key={k} value={k} data-testid={`status-option-${k}`}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <button onClick={openPrintablePdf} data-testid="print-pdf-button" className="btn-gs-secondary !py-3">
                <Printer className="w-5 h-5" /> Stampa PDF
              </button>
              {p.status === "chiusa" && p.zip_storage_path && (
                <button onClick={downloadZip} data-testid="download-zip-button" className="btn-gs-primary !py-3">
                  <Download className="w-5 h-5" /> Scarica ZIP
                </button>
              )}
            </div>
          </div>

          {/* Signature status */}
          <div className="flex gap-2 flex-wrap items-center">
            <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold border ${
              p.signature_otp_verified ? "bg-green-50 text-green-700 border-green-200" : "bg-slate-50 text-slate-600 border-slate-200"
            }`}>
              <ShieldCheck className="w-3 h-3" /> OTP {p.signature_otp_verified ? "verificato" : "in attesa"}
            </span>
            <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold border ${
              p.signature_email_confirmed ? "bg-green-50 text-green-700 border-green-200" : "bg-slate-50 text-slate-600 border-slate-200"
            }`}>
              <Mail className="w-3 h-3" /> Email {p.signature_email_confirmed ? "confermata" : "in attesa"}
            </span>
            {!p.signature_otp_verified && (
              <button onClick={sendOtpRequest} data-testid="request-otp-button"
                className="text-xs font-bold text-[#F77F2A] hover:underline ml-2">
                Invia richiesta firma al cliente →
              </button>
            )}
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-5">
          {/* Client */}
          <div className="card-gs lg:col-span-1">
            <h2 className="text-sm uppercase tracking-wider font-bold text-slate-500 mb-3">Cliente</h2>
            <div className="space-y-3">
              <div>
                <div className="text-xl font-black text-[#1E293B]">{p.client_first_name} {p.client_last_name}</div>
              </div>
              <a href={`tel:${p.client_phone}`} className="flex items-center gap-2 text-sm hover:text-[#2456C6]">
                <Phone className="w-4 h-4 text-slate-400" /> {p.client_phone}
              </a>
              <a href={`mailto:${p.client_email}`} className="flex items-center gap-2 text-sm hover:text-[#2456C6] break-all">
                <Mail className="w-4 h-4 text-slate-400 flex-shrink-0" /> {p.client_email}
              </a>
              <div className="flex items-center gap-2 text-sm">
                <Car className="w-4 h-4 text-slate-400" />
                <span className="font-mono font-bold uppercase">{p.vehicle_plate}</span>
              </div>
            </div>
          </div>

          {/* GPS */}
          <div className="card-gs lg:col-span-2">
            <h2 className="text-sm uppercase tracking-wider font-bold text-slate-500 mb-3">Posizione</h2>
            {p.latitude && p.longitude ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2 font-mono text-sm">
                  <MapPin className="w-4 h-4 text-[#F77F2A]" />
                  {p.latitude.toFixed(5)}, {p.longitude.toFixed(5)}
                </div>
                <iframe
                  title="Mappa"
                  src={`https://www.google.com/maps?q=${p.latitude},${p.longitude}&z=16&output=embed`}
                  className="w-full h-64 rounded-xl border border-slate-200"
                />
                <a href={`https://maps.google.com/?q=${p.latitude},${p.longitude}`} target="_blank" rel="noreferrer"
                  data-testid="open-gmaps-link"
                  className="text-sm font-bold text-[#2456C6] hover:underline inline-flex items-center gap-1">
                  Apri su Google Maps <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            ) : (
              <div className="text-sm text-slate-500">Posizione GPS non disponibile.</div>
            )}
          </div>

          {/* Description */}
          <div className="card-gs lg:col-span-3">
            <h2 className="text-sm uppercase tracking-wider font-bold text-slate-500 mb-3">Descrizione</h2>
            <p className="text-[#1E293B] whitespace-pre-line">{p.description || "Nessuna descrizione fornita."}</p>
          </div>

          {/* Photos */}
          <div className="card-gs lg:col-span-3">
            <h2 className="text-sm uppercase tracking-wider font-bold text-slate-500 mb-3">
              Fotografie ({photos.filter((m) => (m.media_type || "photo") === "photo").length})
            </h2>
            {photos.filter((m) => (m.media_type || "photo") === "photo").length === 0 ? (
              <div className="text-sm text-slate-500">Nessuna foto allegata.</div>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
                {photos.filter((m) => (m.media_type || "photo") === "photo").map((ph) => (
                  <button key={ph.id} onClick={() => setLightbox(fileUrl(ph.storage_path))}
                    data-testid={`photo-thumb-${ph.id}`}
                    className="aspect-square rounded-xl overflow-hidden border border-slate-200 hover:border-[#F77F2A] transition">
                    <img src={fileUrl(ph.storage_path)} alt={ph.original_filename} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Videos */}
          {photos.filter((m) => m.media_type === "video").length > 0 && (
            <div className="card-gs lg:col-span-3">
              <h2 className="text-sm uppercase tracking-wider font-bold text-slate-500 mb-3">
                Video ({photos.filter((m) => m.media_type === "video").length})
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {photos.filter((m) => m.media_type === "video").map((v) => (
                  <div key={v.id} data-testid={`video-${v.id}`}>
                    <video src={fileUrl(v.storage_path)} controls className="w-full rounded-xl bg-black" />
                    <div className="text-xs text-slate-500 mt-1">{v.original_filename} · {(v.size/1024/1024).toFixed(1)}MB</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Audio */}
          {photos.filter((m) => m.media_type === "audio").length > 0 && (
            <div className="card-gs lg:col-span-3">
              <h2 className="text-sm uppercase tracking-wider font-bold text-slate-500 mb-3">
                Audio / Memo vocali ({photos.filter((m) => m.media_type === "audio").length})
              </h2>
              <div className="space-y-2">
                {photos.filter((m) => m.media_type === "audio").map((a) => (
                  <div key={a.id} data-testid={`audio-${a.id}`} className="p-3 bg-slate-50 rounded-xl">
                    <audio src={fileUrl(a.storage_path)} controls className="w-full" />
                    <div className="text-xs text-slate-500 mt-1">{a.original_filename}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4" onClick={() => setLightbox(null)}>
          <button onClick={() => setLightbox(null)} className="absolute top-4 right-4 text-white p-2">
            <X className="w-8 h-8" />
          </button>
          <img src={lightbox} alt="" className="max-w-full max-h-full object-contain" />
        </div>
      )}
    </DashboardLayout>
  );
}
